import type { ReactNode } from 'react';

import { PlatformIcon } from '../components/PlatformIcon.js';
import { ADVERTISED_PLATFORMS } from '../components/advertised-platforms.js';
import './auth.css';

/**
 * Shared frame for the unauthenticated pages (ARCHITECTURE.md §18, §65).
 *
 * §65's restraint is written for OPERATIONAL surfaces — a console someone
 * stares at while running a broadcast, where decoration competes with
 * information. This is not one of those. It is the first thing a person sees,
 * and it has one job the Console never has: explaining what this product is.
 *
 * So it gets a little warmth. Still no images, no external fonts, no blur
 * behind moving content — the ambient wash is two CSS gradients on a fixed
 * layer, which composites once and never repaints.
 *
 * The platform marks are the honest part. They are the platforms Connaro can
 * ACTUALLY connect, derived from one list that a test asserts against the
 * server's catalog — §18 forbids implying support that does not exist, and a
 * sign-in page promising eight logos while five work would be exactly that.
 */
export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="auth">
      {/* Decorative only, and marked so a screen reader skips it entirely. */}
      <div className="auth__ambient" aria-hidden="true" />

      <div className="auth__grid">
        <aside className="auth__pitch">
          <div className="auth__wordmark">
            Connaro<span aria-hidden="true">.</span>
          </div>
          <p className="auth__tagline">Streaming operations platform</p>

          <p className="auth__lede">
            One private console for the chat from every platform you broadcast to. Your team sees
            it; your audience never does.
          </p>

          <h2 className="auth__pitch-heading">Connects to</h2>
          <ul className="auth__platforms">
            {ADVERTISED_PLATFORMS.map((entry) => (
              <li
                key={entry.platform}
                className={`auth__platform platform-card--${entry.platform}`}
              >
                <PlatformIcon platform={entry.platform} displayName={entry.displayName} />
                <span>{entry.displayName}</span>
              </li>
            ))}
          </ul>

          <p className="auth__note">
            {/*
             * Said plainly on the way in, rather than discovered later. A
             * product that overstates what it connects to loses more trust at
             * the moment of discovery than it ever gained at signup.
             */}
            More platforms are in progress. Connaro connects only through official APIs.
          </p>
        </aside>

        <div className="auth__card">
          <section className="panel" aria-labelledby="auth-heading">
            <div className="panel__header">
              <h1 className="panel__title" id="auth-heading">
                {title}
              </h1>
            </div>
            <div className="panel__body">
              {subtitle === undefined ? null : <p className="auth__subtitle">{subtitle}</p>}
              {children}
            </div>
          </section>

          {footer === undefined ? null : <div className="auth__footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
